import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useFact, PACKAGING_ITEMS } from "@/lib/factoryI18n";
import { Link } from "react-router-dom";

export default function DespatchBuilder() {
  const { lang } = useLang();
  const f = useFact(lang);
  const [despatches, setDespatches] = useState([]);
  const [ready, setReady] = useState([]);
  const [current, setCurrent] = useState(null);
  const [addPiece, setAddPiece] = useState("");
  const [checklist, setChecklist] = useState({});
  const [weight, setWeight] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [d, r] = await Promise.all([api.get("/factory/despatches"), api.get("/factory/ready-for-despatch")]);
    setDespatches(d.data);
    setReady(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const r = await api.post("/factory/despatches", { transporter: "", lr_ref: "" });
    setCurrent(r.data);
    load();
  };

  const addToDespatch = async () => {
    setMsg("");
    if (!current) return;
    try {
      await api.post(`/factory/despatches/${current.id}/add-piece`, {
        piece_id: addPiece, checklist, weight_kg: Number(weight) || 0,
      });
      setAddPiece(""); setChecklist({}); setWeight("");
      const r = await api.get(`/factory/despatches/${current.id}`);
      setCurrent(r.data);
      load();
    } catch (e) { setMsg("Error: " + (e?.response?.data?.detail || e.message)); }
  };

  const approve = async () => {
    try {
      await api.post(`/factory/despatches/${current.id}/approve`);
      setMsg("Despatch approved.");
      setCurrent(null); load();
    } catch (e) { setMsg("Error: " + (e?.response?.data?.detail || e.message)); }
  };

  const allChecked = PACKAGING_ITEMS.every((it) => !!checklist[it.key]);

  return (
    <div data-testid="despatch-builder">
      <h1 className="text-2xl font-bold mb-4" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>{f("despatch_builder")}</h1>

      {!current ? (
        <div className="flex gap-2 mb-4">
          <button data-testid="despatch-new" onClick={create} className="px-4 py-2 rounded-full font-semibold text-sm" style={{ background: "var(--brand-navy)", color: "white" }}>+ New Despatch</button>
        </div>
      ) : (
        <div className="bg-white border border-[#1B3A5C]/10 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono font-bold" data-testid="despatch-number">{current.despatch_number}</div>
              <div className="text-xs text-[#7C8E9E]">{current.pieces?.length || 0} piece(s) added</div>
            </div>
            <div className="flex gap-2">
              {current.pieces?.length > 0 && (
                <button data-testid="despatch-approve" onClick={approve} className="px-4 py-2 rounded-full font-semibold text-sm" style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}>{f("approve_despatch")}</button>
              )}
            </div>
          </div>

          <div className="border-t border-[#1B3A5C]/10 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E] mb-2">Add piece</div>
            <div className="flex gap-2 flex-wrap items-end">
              <select data-testid="despatch-piece-picker" value={addPiece} onChange={(e) => setAddPiece(e.target.value)} className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm min-w-[300px]">
                <option value="">Select ready piece…</option>
                {ready.flatMap((g) => g.ready).map((p) => (
                  <option key={p.id} value={p.id}>{p.code} · {p.design_code} · {p.size_text}</option>
                ))}
              </select>
              <input data-testid="despatch-weight" value={weight} onChange={(e) => setWeight(e.target.value)} type="number" placeholder="Weight (kg)" className="h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm w-32" />
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1">
              {PACKAGING_ITEMS.map((it) => (
                <label key={it.key} className="text-xs flex items-center gap-2">
                  <input data-testid={`despatch-check-${it.key}`} type="checkbox" checked={!!checklist[it.key]} onChange={(e) => setChecklist({ ...checklist, [it.key]: e.target.checked })} />
                  {lang === "hi" ? it.hi : it.en}
                </label>
              ))}
            </div>
            <button
              data-testid="despatch-add-piece"
              disabled={!addPiece || !allChecked}
              onClick={addToDespatch}
              className="mt-3 px-4 py-2 rounded-full font-semibold text-sm disabled:opacity-50"
              style={{ background: "var(--brand-navy)", color: "white" }}
            >Add to despatch</button>
            {msg && <div className="text-xs mt-2">{msg}</div>}
          </div>

          {current.pieces?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E] mb-1">Pieces in this despatch</div>
              {current.pieces.map((e) => (
                <div key={e.piece_id} className="text-sm" data-testid={`despatch-piece-${e.piece_id}`}>{e.code} · {e.weight_kg} kg</div>
              ))}
            </div>
          )}
        </div>
      )}

      <h2 className="font-bold mt-6 mb-2" style={{ color: "var(--brand-navy)" }}>{f("ready_despatch")}</h2>
      <div className="space-y-2">
        {ready.map((g) => (
          <div key={g.po_id || "no-po"} className="bg-white border border-[#1B3A5C]/10 rounded-lg p-3" data-testid={`ready-group-${g.po_id || "no-po"}`}>
            <div className="text-sm font-semibold">{g.po_id ? `PO ${g.po_id.slice(0, 8)}` : "No PO linked"} — <span className="font-mono">{g.ready.length}</span> ready of <span className="font-mono">{g.total || g.ready.length}</span></div>
            <div className="text-xs text-[#7C8E9E]">{g.ready.map((p) => p.code).join(", ")}</div>
          </div>
        ))}
        {ready.length === 0 && <div className="text-xs text-[#7C8E9E]">No pieces ready.</div>}
      </div>

      <h2 className="font-bold mt-6 mb-2" style={{ color: "var(--brand-navy)" }}>Recent despatches</h2>
      <div className="space-y-1">
        {despatches.slice(0, 10).map((d) => (
          <div key={d.id} className="text-sm" data-testid={`despatch-list-${d.id}`}>
            <span className="font-mono">{d.despatch_number}</span> · {d.status} · {d.pieces?.length || 0} piece(s)
          </div>
        ))}
      </div>
    </div>
  );
}
